/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { BrushState } from 'src/luis/modules/labeling/components/Brush';
import { isMachineLearned } from 'src/luis/projects/entities/utils/entityHelpers';
import { ModelType } from 'src/luis/types/ModelType';
import { EntityRoleStore } from 'src/luis/types/stores/entities/EntityRoleStore';
import { EntityStore } from 'src/luis/types/stores/entities/EntityStore';
import { SimpleEntityStore } from 'src/luis/types/stores/entities/SimpleEntityStore';

type BrushInput = {
    data: { entity?: EntityStore; parentEntity?: EntityStore; role?: EntityRoleStore; normalizedValue?: string };
    hoveredEntitiesIds: string[];
};

export const resolveBrushRules = ({ data, hoveredEntitiesIds }: BrushInput): BrushState => {
    if (!data) {
        return 'noSelection';
    }

    if (!hoveredEntitiesIds) {
        return 'valid';
    }

    const { entity, parentEntity } = data;
    const isNonMLEntity = !isMachineLearned(entity.typeId);

    if (entity && parentEntity && entity.typeId === ModelType.Simple && parentEntity.typeId === ModelType.Simple) {
        return nDepthRules({ data, hoveredEntitiesIds });
    }

    if (isNonMLEntity) {
        return nonMLEntityRules({ data, hoveredEntitiesIds });
    }

    return 'valid';
};

/**
 * Decides the brush state when the entity is nDepth child based on the following:
 * 1) If the direct parent of the entity exists in the hovered entities array.
 * 2) If the entity has a constraints (instance) applied to it, it has be included in the
 * hovered entities array.
 */
const nDepthRules = ({ data: { entity }, hoveredEntitiesIds }: BrushInput): BrushState => {
    const childEntity = <SimpleEntityStore>entity;

    if (childEntity.instanceOf && !hoveredEntitiesIds.find(id => id === childEntity.instanceOf)) {
        return 'noInstanceOf';
    }

    return 'valid';
};

/**
 * Decides the brush state when the entity is non-ml based on the following:
 * 1) If the entity is list entity delegate the resolution to `listEntityRules`.
 * 2) If the entity has a role, it has be included in the
 * hovered entities array.
 * 3) If no role is applied, the user has to select a role first.
 */
const nonMLEntityRules = (input: BrushInput): BrushState => {
    const {
        data: { entity, role },
        hoveredEntitiesIds
    } = input;
    if (entity.typeId === ModelType.List) {
        return listEntityRules(input);
    }

    if (role) {
        const foundLabel = Boolean(hoveredEntitiesIds.find(id => id === entity.id));
        if (!foundLabel) {
            return 'nonMLNotFired';
        }
    } else {
        return 'nonMLNoRoleApplied';
    }

    return 'valid';
};

/**
 * Decides the brush state when the entity is of type list based on the following:
 * 1) If there is a normalized value selected, then it is a valid state.
 * 2) If there is no normalized value selected, check if there is a role selected and
 * the list entity is included in the hovered entities array.
 */
const listEntityRules = ({ data: { entity, normalizedValue, role }, hoveredEntitiesIds }: BrushInput): BrushState => {
    let brushState: BrushState = 'valid';
    const foundEntity = Boolean(hoveredEntitiesIds.find(id => id === entity.id));
    if (!normalizedValue) {
        brushState = 'listEntityNoNormalizedVal';
        if (!role && foundEntity) {
            brushState = 'nonMLNoRoleApplied';
        } else if (foundEntity) {
            brushState = 'valid';
        }
    }

    return brushState;
};
